import {
  voiceGradientStyle,
  type VoiceGradientStatus,
} from "@/components/cursors/voice-gradient-style";
import { VoiceGradientReflection } from "@/components/cursors/voice-gradient-reflection";
import { useDictation } from "./use-dictation";
import { useSmoothedVoiceVolume } from "./use-smoothed-voice-volume";
import type { CompactVoicePhase } from "./use-compact-voice-status";
import { useT } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

export function CompactDenkerVoiceStatus({
  active,
  agentErrorLabel,
  commandLabel,
  expanded,
  live,
  phase,
  voiceErrorLabel,
  screenshotIntent,
  showLabel,
  status,
}: {
  active: boolean;
  agentErrorLabel?: string;
  commandLabel?: string | null;
  expanded: boolean;
  live: boolean;
  phase: CompactVoicePhase;
  voiceErrorLabel?: string;
  screenshotIntent: boolean;
  showLabel: boolean;
  status: VoiceGradientStatus;
}) {
  const t = useT(compactMessages);
  const rawVolume = useDictation((state) => state.volume);
  const showingAgentError = !active && Boolean(agentErrorLabel);
  // The voice error text renders in full in the widget's in-flow error box
  // (which wraps instead of clipping); the pill keeps only the red gradient
  // so the message is never shown cropped. Screen readers still get it via
  // the aria-label below.
  const showingVoiceError = !showingAgentError && phase === "error";
  const gradientActive = active || showingAgentError;
  const volume = useSmoothedVoiceVolume(rawVolume, gradientActive);
  if (!gradientActive) return null;
  const quietLive = active && live && phase === "live";
  const label = showingAgentError
    ? (agentErrorLabel ?? t("agentError"))
    : phase === "error"
      ? (voiceErrorLabel ?? t("voiceUnavailable"))
      : phase === "speaking"
        ? t("phaseSpeaking")
        : commandLabel
          ? commandLabel
          : phase === "starting"
            ? t("phaseStartingLive")
            : phase === "sending"
              ? t("phaseSending")
              : phase === "sent"
                ? t("phaseSent")
                : phase === "live"
                  ? t("phaseLive")
                  : t("phaseListening");
  const ariaLabel =
    screenshotIntent && active ? t("screenshotAttachmentOn", { label }) : label;
  const visualStatus = showingAgentError ? "error" : status;

  return (
    <div
      className={
        expanded
          ? "pointer-events-none absolute top-1 z-0 h-7 w-7 overflow-hidden rounded-full"
          : "pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-pill"
      }
      data-testid="desktop-compact-voice-status"
      data-source={showingAgentError ? "agent" : "voice"}
      data-live={live ? "true" : undefined}
      data-phase={showingAgentError ? "error" : phase}
      data-status={quietLive ? "live" : visualStatus}
      role="status"
      aria-label={ariaLabel}
      style={{
        ...(quietLive
          ? {
              backgroundColor:
                "color-mix(in srgb, var(--color-primary) 8%, transparent)",
            }
          : voiceGradientStyle(visualStatus, "var(--color-accent)", volume)),
        ...(expanded
          ? { left: "calc(var(--denker-logo-center, 96px) - 14px)" }
          : {}),
      }}
    >
      {!quietLive && (
        <VoiceGradientReflection testId="desktop-compact-voice-reflection" />
      )}
      {!showingAgentError && !showingVoiceError && !expanded && showLabel && (
        <span
          className="absolute left-9 right-2 top-0 flex h-9 items-center text-appkit-caption font-semibold text-[var(--color-readable-overlay-primary)]"
          data-testid="desktop-compact-voice-label"
        >
          <span className="min-w-0 truncate">{label}</span>
        </span>
      )}
    </div>
  );
}
